/**
 * 오픈 알림 신청 mock 저장소.
 *
 * 주소별 신청 상태(이미 신청 여부 + 대기 인원수)를 메모리에 보관.
 * dev 환경에서 GET/POST 흐름을 실제 백엔드처럼 시뮬레이션:
 *  - GET: 주소 조회 시 저장된 상태 반환 (없으면 미신청 default)
 *  - POST: 같은 주소에 대한 entry 갱신 (waitingCount +1, isCompleted=true, completedAt 현재 시각)
 *
 * 페이지 새로고침 시 mock 상태 초기화 — 메모리 변수만 사용.
 */

import type { WaitlistStatus } from '@/types/serviceArea'

interface MockState {
  waitingCount: number
  /** 사용자(현재 mock 사용자)가 이미 신청했는지 */
  isCompleted: boolean
  /** 신청 시각 (ISO string in mock, "yyyy-MM-dd HH:mm:ss" 형식) */
  completedAt: string | null
}

/** 주소 → 상태. 페이지 새로고침 시 초기화 */
const mockStore = new Map<string, MockState>([
  // sample 데이터: 청주시 상당구 하하동 — Figma 디자인 예시 주소
  ['청주시 상당구 하하동', { waitingCount: 152, isCompleted: false, completedAt: null }],
])

const formatDateTime = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

/**
 * 신규 주소 default 대기 인원 — 0으로 두어 "아직 아무도 요청하지 않았어요" 케이스 시연 가능.
 * 사용자가 신청하면 +1로 증가 (applyMockWaitlistRequest).
 * 152명 같은 sample 카운트는 mockStore에 미리 박아둔 entry로 시연.
 */
const DEFAULT_WAITING_COUNT = 0

/** GET 응답 — 저장된 상태 또는 default */
export const findMockWaitlistStatus = (address: string): WaitlistStatus => {
  const state = mockStore.get(address) ?? {
    waitingCount: DEFAULT_WAITING_COUNT,
    isCompleted: false,
    completedAt: null,
  }
  return { address, ...state }
}

/** POST 응답 — entry 갱신 (waitingCount +1, isCompleted=true) */
export const applyMockWaitlistRequest = (address: string): WaitlistStatus => {
  const prev = mockStore.get(address) ?? {
    waitingCount: DEFAULT_WAITING_COUNT,
    isCompleted: false,
    completedAt: null,
  }
  const next: MockState = prev.isCompleted
    ? prev // 이미 신청한 사용자 — 백엔드는 409 던지지만 mock은 단순화 (정책 미정)
    : {
        waitingCount: prev.waitingCount + 1,
        isCompleted: true,
        completedAt: formatDateTime(new Date()),
      }
  mockStore.set(address, next)
  return { address, ...next }
}
